// Types
import type { ArticlePreview } from 'interfaces/content/articles/Articles';

// External libs
import classNames from 'classnames';
import { getDevicePixelRatio } from 'use-device-pixel-ratio';

// Constants & utils
import { FEATURED_SECONDARY_TEST_IDS } from 'constants/testsIds/homepage';
import { setEventAction } from 'utils/trackDataUtils';

// Components
import WellHomePageInStream from 'components/Ad/AdSlot/ProductHomePageInStream';
import ArticleCard from 'components/Globals/ArticleCard';
import Link from 'components/Globals/Base/Link';
import FeaturedArticle from 'components/Home/FeaturedArticle';
import Row from 'components/Grid/Row';

// Local
import * as S from './FeaturedSecondaryStyles';

export interface FeaturedSecondaryProps {
  className?: string;
  heading?: string;
  headingLink?: string;
  dataSource?: string;
  dataCy?: string;
  featuredArticle?: ArticlePreview;
  articles: ArticlePreview[];
  hideAds?: boolean;
  type?: 'home' | 'product-home';
}

const FeaturedSecondary = ({
  className,
  heading,
  featuredArticle,
  articles,
  headingLink,
  dataCy,
  hideAds,
  type = 'home',
}: FeaturedSecondaryProps) => {
  if (!featuredArticle && articles.length < 1) {
    return null;
  }

  const isEuropeFinanceSection = heading === 'Finance';
  const dpr = getDevicePixelRatio({ round: false }) === 2.125 || getDevicePixelRatio({ round: false }) === 1.875;
  const fontSize = dpr ? 'level4' : 'level3';

  return (
    <S.Wrapper
      data-cy={dataCy || `${FEATURED_SECONDARY_TEST_IDS.FEATURED_SECONDARY}-${heading || 'no-heading'}`}
      className={classNames(className, {
        noHeading: !heading,
      })}
    >
      {heading && (
        <S.Title
          className={`${featuredArticle ? '' : 'noFeatured'}`}
          as='h2'
          $fontFamily='saol'
          $size={{ default: 'level3', lgMin: 'level2' }}
          data-cy={FEATURED_SECONDARY_TEST_IDS.TITLE}
        >
          {headingLink ? (
            <Link
              href={headingLink}
              type='primary'
            >
              {heading}
            </Link>
          ) : (
            heading
          )}
        </S.Title>
      )}
      {featuredArticle && (
        <S.Featured>
          <FeaturedArticle {...featuredArticle} />
        </S.Featured>
      )}
      <S.GridWrapper>
        <Row>
          {articles.map((article) => (
            <li key={article.title}>
              <ArticleCard
                article={article}
                eyebrowType='red'
                layoutVariant='hero'
                dataVariant={`${type}-featured-secondary`}
                titleSize={{ default: fontSize }}
                trackingData={{
                  contentPlacementCD: article.eyebrowSectionLabel,
                  eventAction: setEventAction(article.eyebrowSectionLabel, article.eyebrowTagLabel),
                  eventCategory: 'article',
                  eventLabel: article.title,
                }}
              />
            </li>
          ))}
        </Row>
      </S.GridWrapper>
      {!hideAds && isEuropeFinanceSection && <WellHomePageInStream id={0} />}
    </S.Wrapper>
  );
};

export default FeaturedSecondary;
